import { Modal, Text } from '@mantine/core';
import type { FC, ReactNode } from 'react';

import classes from './MacWindowModal.module.css';

type Props = {
    opened: boolean;
    onClose: () => void;
    /** Centred in the title bar, as a native window titles itself. */
    title: ReactNode;
    children: ReactNode;
    /** Width in px. Defaults to 720 — wide enough for four columns of detail. */
    size?: number;
};

/**
 * A macOS-style window, used wherever a panel is a document you read and close rather than
 * a rail you keep open beside your work.
 *
 * The close light is the point: it is the obvious affordance without a labelled button, and
 * the shape tells you this is a thing that sits ON the page rather than part of it. Only the
 * red one is rendered — minimise and zoom have nothing to do in a modal, and a control that
 * looks clickable but is inert is worse than one that is simply not there.
 */
const MacWindowModal: FC<Props> = ({ opened, onClose, title, children, size = 720 }) => (
    <Modal.Root opened={opened} onClose={onClose} centered size={size} padding={0} radius={12}>
        <Modal.Overlay backgroundOpacity={0.35} blur={2} />
        <Modal.Content className={classes.window}>
            <div className={classes.titlebar}>
                <div className={classes.lights}>
                    {/* Close only. A modal has nowhere to minimise to and no larger size to
                        zoom into, so amber and green would be decoration that looks
                        clickable — worse than absent. */}
                    <button
                        type="button"
                        aria-label="Close"
                        className={`${classes.light} ${classes.close}`}
                        onClick={onClose}
                    >
                        <span className={classes.closeGlyph}>✕</span>
                    </button>
                </div>
                <div className={classes.title}>
                    <Text size="13px" fw={600}>
                        {title}
                    </Text>
                </div>
            </div>
            <Modal.Body className={classes.body}>{children}</Modal.Body>
        </Modal.Content>
    </Modal.Root>
);

export default MacWindowModal;
export { classes as macWindowClasses };
